import clsx from "clsx";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { Intercom } from "src/js/util/intercom";
import { InsightStatusType } from "react-migration/components/Insight/components/Body/Body";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Notification } from "react-migration/components/Notification";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { Insight } from "react-migration/components/Insight/Insight";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import {
  ThirdPartyOrder,
  ThirdPartyOrderStatus,
} from "react-migration/domains/sites/components/SiteCard/ReportSidebar/AiDash/hooks/useThirdPartyOrder";
import AiDashLogo from "public/addons/aidash/aidash-logo.svg";
import { ManagementFilesLayerTabUK, ManagementLayerTabUK } from "../../../Bundle/Workbench/ManagmentTab/types";
import { ModalImagePreview } from "../../../Bundle/Workbench/ManagmentTab/components/ModalImagePreview";
import { calculateDaysOverdue } from "../util";
import { useGeoJsonDeliverableMapLayer } from "./useGeoJsonDeliverableMapLayer";
import { GeoJsonMetadataTables } from "./GeoJsonMetadataTables";

type ThirdPartyOnDemandOrderProps = {
  order: ThirdPartyOrder;
  providerName: string;
  smallCardView?: boolean;
};

export function ThirdPartyOnDemandOrder({ order, providerName, smallCardView = false }: ThirdPartyOnDemandOrderProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [previewOpened, setPreviewOpened] = useState<boolean>(false);

  const pdfUrl = order?.deliverables?.find((deliverable) => deliverable.type === "PDF")?.signedUrl?.url;
  const geojsonUrl = order?.deliverables?.find((deliverable) => deliverable.type === "GEOJSON")?.signedUrl?.url;
  const pdfFileName = order?.deliverables?.find((deliverable) => deliverable.type === "PDF")?.filename;
  const orderCompleted = order.status === ThirdPartyOrderStatus.COMPLETED;
  const pdfReportReady = orderCompleted && pdfUrl;
  const geojsonReportReady = orderCompleted && geojsonUrl;

  const handleOpenPdfReport = useCallback(() => {
    if (!isMobile) {
      setPreviewOpened(true);
    } else {
      window.open(pdfUrl, "_blank");
    }
  }, [pdfUrl, isMobile]);

  const onContactRequest = useCallback((message?: string) => {
    Intercom("showNewMessage", message ? message : "");
  }, []);

  const handleReportOverdue = useCallback(() => {
    onContactRequest(
      "Hello, I noticed that my BNGAI™ report is past due and has not yet been completed. Could you please assist me in resolving this issue? Report ID: " +
        order.id
    );
  }, [onContactRequest, order.id]);

  const { daysOverdue, isOverdue } = calculateDaysOverdue(order.estimatedCompletedAt || "", order.status);

  return (
    <div
      className={clsx("flex flex-col justify-between items-center gap-4", {
        "p-4": !smallCardView,
      })}
    >
      <div className="flex items-center">
        <div className="flex flex-col gap-y-1">
          <h3 className="text-base font-semibold text-text-base-primary">
            {t(`third_party_on_demand_report_layer.${providerName}.selection_sidebar.card_header.${order.status}`)}
          </h3>
          <span className="text-xs text-text-base-secondary-a">
            {t(`third_party_on_demand_report_layer.${providerName}.selection_sidebar.card_subtitle.${order.status}`)}
          </span>
        </div>
        <div className="p-4 w-[112px]">
          <img className="w-full" src={AiDashLogo} alt="AiDash" />
        </div>
      </div>

      <Insight.Wrapper title="Report status" className="w-full">
        {pdfReportReady ? (
          <Insight.Body status="green">
            <div className="flex items-center justify-between w-full">
              <a
                role="button"
                tabIndex={0}
                className="appearance-none cursor-pointer underline! text-text-base-active hover:text-text-base-active-hover!"
                onClick={handleOpenPdfReport}
                onKeyDown={(e) => e.key === "Enter" && handleOpenPdfReport()}
              >
                Ready to view
              </a>
              <Link
                className="leading-none select-none cursor-pointer text-sm no-underline text-text-base-active hover:text-text-base-active-hover!"
                to={`/management/${ManagementLayerTabUK.FILES}/${ManagementFilesLayerTabUK.PURCHASED}`}
              >
                <span className="mr-1">Go to files</span>
                <Icon icon="icon-lt-arrow-right-line" size="sm" cursor="pointer" />
              </Link>
            </div>
          </Insight.Body>
        ) : (
          <Insight.Body {...getInsightBodyTypeProps(order.status)} />
        )}
        {[ThirdPartyOrderStatus.PLACED, ThirdPartyOrderStatus.PROCESSING].includes(order.status) ? (
          <Insight.Footer>
            <span>Estimated completion: </span>
            <span className="">
              {order.estimatedCompletedAt ? new Date(order.estimatedCompletedAt).toLocaleDateString() : "Unknown"}
            </span>
          </Insight.Footer>
        ) : [ThirdPartyOrderStatus.COMPLETED].includes(order.status) ? (
          <Insight.Footer>
            <span>Delivered on: </span>
            <span>{order.completedAt ? new Date(order.completedAt).toLocaleDateString() : "Unknown"}</span>
          </Insight.Footer>
        ) : null}
      </Insight.Wrapper>

      {isOverdue && (
        <Notification.Info
          title={`Report is ${daysOverdue} overdue`}
          message={
            <span>
              Your report is past due and has not been completed. Please{" "}
              <span
                className="text-platform-600 cursor-pointer"
                onClick={handleReportOverdue}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleReportOverdue()}
              >
                {t(`third_party_on_demand_report_layer.${providerName}.selection_sidebar.contact_us`)}
              </span>{" "}
              for assistance.
            </span>
          }
        />
      )}

      {orderCompleted && (!pdfReportReady || !geojsonReportReady) && (
        <Notification.Info
          title={`There may have been an issue with your order`}
          message={
            <span>
              {order?.provider?.displayName} has marked your order as completed, but one or more of the deliverables are
              missing. Please{" "}
              <span
                className="text-platform-600 cursor-pointer"
                onClick={handleReportOverdue}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleReportOverdue()}
              >
                {t(`third_party_on_demand_report_layer.${providerName}.selection_sidebar.contact_us`)}
              </span>{" "}
              for assistance.
            </span>
          }
        />
      )}

      {geojsonReportReady ? (
        <>
          <GeoJsonMetadataTables signedUrl={geojsonUrl} providerName={providerName} />
          <ViewOnMapButton signedUrl={geojsonUrl} />
        </>
      ) : (
        <Button variant="ghost" size="md" onClick={() => onContactRequest("Enquiry about BNGAI™ report purchase")}>
          Contact us
        </Button>
      )}

      {previewOpened && (
        <ModalImagePreview
          url={pdfUrl || ""}
          extension={"pdf"}
          fileName={pdfFileName || "Preliminary Baseline Report"}
          onDownload={() => window.open(pdfUrl, "_blank")}
          handleClose={() => setPreviewOpened(false)}
        />
      )}
    </div>
  );
}

const getInsightBodyTypeProps = (status: ThirdPartyOrderStatus): { status: InsightStatusType; children?: string } => {
  switch (status) {
    case ThirdPartyOrderStatus.PLACED:
      return { status: "amber", children: "In progress" };
    case ThirdPartyOrderStatus.PROCESSING:
      return { status: "amber", children: "In progress" };
    case ThirdPartyOrderStatus.COMPLETED:
      return { status: "green", children: "Completed" };
    case ThirdPartyOrderStatus.FAILED:
      return { status: "red", children: "Failed" };
    case ThirdPartyOrderStatus.CANCELED:
      return { status: "red", children: "Canceled" };
    default:
      return { status: "amber", children: "Pending" };
  }
};

function ViewOnMapButton({ signedUrl }: { signedUrl: string }) {
  const [viewingOnMap, setViewingOnMap] = useState<boolean>(false);

  const { isLoading, error } = useGeoJsonDeliverableMapLayer(signedUrl, viewingOnMap);

  if (error) {
    return null;
  }

  return (
    <Button
      disabled={isLoading}
      variant={viewingOnMap || isLoading ? "tertiary" : "ghost"}
      size="lg"
      onClick={() => setViewingOnMap(!viewingOnMap)}
    >
      {isLoading ? (
        <Icon icon="icon-lt-loader-4-line" size="md" extraClasses="inline-block animate-spin origin-center" />
      ) : (
        <>
          <Icon icon="icon-lt-road-map-line" cursor="pointer" /> {viewingOnMap ? "Clear map" : "View on map"}
        </>
      )}
    </Button>
  );
}
